import { useEffect, useState } from "react";
import FundingSourceAccessPanel, {
  PendingAccessAddition,
  PendingAccessRemoval,
  PendingAccessUpdate,
} from "admin/components/cost-management/FundingSourceAccessPanel";
import { COST_MANAGER_WRITE_PERMISSION } from "admin/components/cost-management/FundingSources";
import useFundingSourceAccess from "admin/hooks/useFundingSourceAccess";
import useFundingSources from "admin/hooks/useFundingSources";
import { FundingSource } from "admin/types/interfaces/funding";
import { Loader2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "~/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";
import useToast from "~/components/ui/toast";
import useUserPermission from "~/hooks/useUserPermission";
import { ACCESS_CONTROLLER_V1 } from "~/utils/accessController";
import { trackEvent } from "~/utils/monitoring";

interface FundingSourceDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  fundingSource: FundingSource | null;
}

const FundingSourceDrawer = ({ isOpen, onClose, fundingSource }: FundingSourceDrawerProps) => {
  const { showSuccess, showError } = useToast();
  const canEditAdminApp = useUserPermission(COST_MANAGER_WRITE_PERMISSION);
  const { mutate } = useFundingSources(true);
  const { mutate: mutateAccess, data: accessData } = useFundingSourceAccess(
    canEditAdminApp ? (fundingSource?.id ?? null) : null
  );
  const canEditFundingSource = !!accessData; // accessData is null if user is not admin of funding source, only admins can edit funding source

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);

  // Pending changes (batched until Save)
  const [pendingName, setPendingName] = useState<string | null>(null);
  const [pendingFundingAccountId, setPendingFundingAccountId] = useState<string | null>(null);
  const [pendingAccessAdditions, setPendingAccessAdditions] = useState<PendingAccessAddition[]>([]);
  const [pendingAccessRemovals, setPendingAccessRemovals] = useState<PendingAccessRemoval[]>([]);
  const [pendingAccessUpdates, setPendingAccessUpdates] = useState<PendingAccessUpdate[]>([]);

  // Reset form state when drawer opens or funding source changes
  useEffect(() => {
    if (isOpen) {
      setPendingName(null);
      setPendingFundingAccountId(null);
      setPendingAccessAdditions([]);
      setPendingAccessRemovals([]);
      setPendingAccessUpdates([]);
    }
  }, [isOpen, fundingSource?.id]);

  if (!fundingSource) return null;

  const baseUrl = `${ACCESS_CONTROLLER_V1}/funding-sources/${fundingSource.id}`;

  const hasPendingChanges =
    (pendingName !== null && pendingName.trim() !== fundingSource.name) ||
    (pendingFundingAccountId !== null &&
      (pendingFundingAccountId ?? "").trim() !== (fundingSource.fundingAccountId ?? "")) ||
    pendingAccessAdditions.length > 0 ||
    pendingAccessRemovals.length > 0 ||
    pendingAccessUpdates.length > 0;

  const resetForm = () => {
    setPendingName(null);
    setPendingFundingAccountId(null);
    setPendingAccessAdditions([]);
    setPendingAccessRemovals([]);
    setPendingAccessUpdates([]);
  };

  const handleClose = () => {
    if (hasPendingChanges && canEditFundingSource) {
      setShowUnsavedDialog(true);
    } else {
      onClose();
    }
  };

  const handleConfirmClose = () => {
    setShowUnsavedDialog(false);
    resetForm();
    onClose();
  };

  const handleCancelClose = () => {
    setShowUnsavedDialog(false);
  };

  const handleSave = async () => {
    setIsSubmitting(true);
    try {
      const newName = pendingName !== null ? pendingName.trim() : fundingSource.name;
      const newFundingAccountId =
        pendingFundingAccountId !== null
          ? pendingFundingAccountId.trim()
          : fundingSource.fundingAccountId;

      const hasNameChange = newName !== fundingSource.name;
      const hasIdChange = newFundingAccountId !== fundingSource.fundingAccountId;

      if (!newName.trim()) {
        showError("Name is required");
        return;
      }
      if (hasIdChange && !newFundingAccountId) {
        showError("Id is required");
        return;
      }

      if (hasNameChange || hasIdChange) {
        const res = await fetch(baseUrl, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: newName,
            funding_account_id: newFundingAccountId,
          }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          showError(err.message || err.detail || "Failed to update funding source");
          return;
        }
      }

      for (const addition of pendingAccessAdditions) {
        if (addition.type === "user") {
          const res = await fetch(`${baseUrl}/users`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              email: addition.identifier,
              access: addition.access,
            }),
          });
          if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            showError(err.message || err.detail || "Failed to add user access");
            return;
          }
        } else {
          const res = await fetch(`${baseUrl}/user-tags`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              tag: addition.identifier,
              access: addition.access,
            }),
          });
          if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            showError(err.message || err.detail || "Failed to add group access");
            return;
          }
        }
      }
      for (const update of pendingAccessUpdates) {
        if (update.type === "user") {
          const res = await fetch(`${baseUrl}/users`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              email: update.identifier,
              access: update.access,
            }),
          });
          if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            showError(err?.message || err?.detail || "Failed to update user access");
            return;
          }
        } else {
          const res = await fetch(`${baseUrl}/user-tags`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              tag: update.identifier,
              access: update.access,
            }),
          });
          if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            showError(err?.message || err?.detail || "Failed to update group access");
            return;
          }
        }
      }
      for (const removal of pendingAccessRemovals) {
        if (removal.type === "user") {
          const res = await fetch(`${baseUrl}/users/${encodeURIComponent(removal.identifier)}`, {
            method: "DELETE",
          });
          if (!res.ok) {
            showError("Failed to remove user access");
            return;
          }
        } else {
          const res = await fetch(
            `${baseUrl}/user-tags/${encodeURIComponent(removal.identifier)}`,
            { method: "DELETE" }
          );
          if (!res.ok) {
            showError("Failed to remove group access");
            return;
          }
        }
      }
      showSuccess("Funding source updated");
      resetForm();
      onClose();
      mutate().catch(() => {});
      mutateAccess().catch(() => {});
    } catch {
      showError("Failed to update funding source");
    } finally {
      setIsSubmitting(false);
    }
  };

  // TODO: uncomment this when funding source deletion is implemented
  // const handleDeleteFundingSource = async () => {
  //   setIsDeleting(true);
  //   try {
  //     const res = await fetch(baseUrl, { method: "DELETE" });
  //     if (!res.ok) {
  //       showError("Failed to delete funding source, please try again.");
  //     } else {
  //       showSuccess("Funding source deleted successfully");
  //       await mutate();
  //       setShowDeleteDialog(false);
  //       onClose();
  //     }
  //   } catch {
  //     showError("Failed to delete funding source, please try again");
  //   } finally {
  //     setIsDeleting(false);
  //   }
  // };

  return (
    <>
      <Sheet open={isOpen} onOpenChange={(open) => !open && handleClose()}>
        <SheetContent
          className="w-[750px] sm:max-w-[750px] ml-32 bg-white flex flex-col h-screen gap-0"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <SheetHeader className="flex-shrink-0 pl-8 pr-16 mb-4">
            <SheetTitle className="flex items-center gap-3 text-2xl">
              {fundingSource.name}
            </SheetTitle>
          </SheetHeader>

          <div className="flex-1 flex flex-col overflow-hidden">
            <Tabs defaultValue="details" className="w-full flex-1 flex flex-col overflow-hidden">
              {canEditFundingSource && (
                <div className="mx-8">
                  <TabsList>
                    <TabsTrigger value="details">Details</TabsTrigger>
                    <TabsTrigger
                      value="access"
                      onClick={() =>
                        trackEvent("admin_access_tab_clicked", {
                          type: "funding_source",
                        })
                      }
                    >
                      Access
                    </TabsTrigger>
                  </TabsList>
                </div>
              )}
              <div className="flex-1 overflow-y-auto px-8 pb-6">
                <TabsContent value="details" className="space-y-6 pl-0 mt-6">
                  <div className="space-y-4">
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="fundingSourceName" className="gap-0">
                            Funding Source Name
                            <span className="text-destructive">*</span>
                          </Label>
                          <Input
                            id="fundingSourceName"
                            value={pendingName ?? fundingSource.name}
                            onChange={(e) => setPendingName(e.target.value)}
                            disabled={!canEditFundingSource}
                            placeholder="Enter funding source name"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="fundingAccountId" className="gap-0">
                            Funding Id
                            <span className="text-destructive">*</span>
                          </Label>
                          <Input
                            id="fundingAccountId"
                            value={
                              pendingFundingAccountId !== null
                                ? pendingFundingAccountId
                                : fundingSource.fundingAccountId
                            }
                            onChange={(e) => setPendingFundingAccountId(e.target.value)}
                            disabled={!canEditFundingSource}
                            placeholder="e.g. funding-account-123"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        {fundingSource.createdAt && (
                          <div className="text-muted-foreground text-sm">
                            Created on: {new Date(fundingSource.createdAt).toLocaleDateString()} at{" "}
                            {new Date(fundingSource.createdAt).toLocaleTimeString("en-US", {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </div>
                        )}
                        {fundingSource.modifiedAt && (
                          <div className="text-muted-foreground text-sm">
                            Modified at: {new Date(fundingSource.modifiedAt).toLocaleDateString()}{" "}
                            at{" "}
                            {new Date(fundingSource.modifiedAt).toLocaleTimeString("en-US", {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </TabsContent>
                {canEditFundingSource && (
                  <TabsContent value="access" className="space-y-6 pl-0 mt-6">
                    <FundingSourceAccessPanel
                      fundingSource={fundingSource}
                      pendingAdditions={pendingAccessAdditions}
                      pendingRemovals={pendingAccessRemovals}
                      onAddAccess={(a) => setPendingAccessAdditions((prev) => [...prev, a])}
                      onRemoveAccess={(r) => {
                        const isPending = pendingAccessAdditions.some(
                          (a) => a.type === r.type && a.identifier === r.identifier
                        );
                        if (isPending) {
                          setPendingAccessAdditions((prev) =>
                            prev.filter(
                              (a) => !(a.type === r.type && a.identifier === r.identifier)
                            )
                          );
                        } else {
                          setPendingAccessRemovals((prev) => [...prev, r]);
                        }
                      }}
                      onUpdateAccess={(u) => {
                        const isPendingAddition = pendingAccessAdditions.some(
                          (a) => a.type === u.type && a.identifier === u.identifier
                        );
                        if (isPendingAddition) {
                          setPendingAccessAdditions((prev) =>
                            prev.map((a) =>
                              a.type === u.type && a.identifier === u.identifier
                                ? { ...a, access: u.access }
                                : a
                            )
                          );
                        } else {
                          setPendingAccessUpdates((prev) => {
                            const rest = prev.filter(
                              (x) => !(x.type === u.type && x.identifier === u.identifier)
                            );
                            return [...rest, u];
                          });
                        }
                      }}
                      pendingUpdates={pendingAccessUpdates}
                    />
                  </TabsContent>
                )}
              </div>
            </Tabs>
          </div>

          {canEditFundingSource && (
            <div className="flex-shrink-0 bg-white border-t border-border py-4 px-8">
              <div className="flex items-center justify-between gap-2">
                <div>
                  {/* TODO: uncomment this when funding source deletion is implemented */}
                  {/* <Button variant="destructive" onClick={() => setShowDeleteDialog(true)}>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Funding Source
                  </Button> */}
                </div>
                <div className="flex items-center gap-2">
                  {hasPendingChanges && (
                    <Button variant="outline" onClick={handleClose}>
                      Cancel
                    </Button>
                  )}
                  <Button
                    onClick={() => {
                      trackEvent("admin_funding_source_save_clicked");
                      handleSave();
                    }}
                    disabled={!hasPendingChanges || isSubmitting}
                  >
                    {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    {isSubmitting ? "Saving..." : "Save"}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      <AlertDialog open={showUnsavedDialog} onOpenChange={setShowUnsavedDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Are you sure you want to close without saving?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelClose}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmClose}
              className="bg-destructive hover:bg-destructive/90"
            >
              Close without saving
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* TODO: uncomment this when funding source deletion is implemented */}
      {/* <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Funding Source</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{fundingSource.name}&quot;? This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteFundingSource}
              disabled={isDeleting}
              className="bg-destructive hover:bg-destructive/90"
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog> */}
    </>
  );
};

export default FundingSourceDrawer;
